import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Play, Pause, Download, Share2, Copy, Volume2, VolumeX, SkipBack, SkipForward } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXTwitter, faFacebook, faLinkedin } from '@fortawesome/free-brands-svg-icons';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { theme } from '../styles/theme';
import { trackClick, trackSocialShare, trackError } from '../services/analytics';

const Container = styled.div`
  ${theme.glassContainer.normal}
  max-width: 600px;
  width: 100%;
  margin: 0 auto;
  padding: 32px;
  font-family: ${theme.typography.fontFamily};
  
  @media (max-width: 768px) {
    padding: 20px;
    margin: 0 20px;
    max-width: calc(100% - 40px);
  }
  
  @media (max-width: 480px) {
    padding: 16px;
    margin: 0 20px;
    max-width: calc(100% - 40px);
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 32px;
`;

const Title = styled.h1`
  color: ${theme.colors.text.primary};
  font-size: ${theme.typography.sizes['3xl']};
  font-weight: ${theme.typography.weights.bold};
  margin: 0 0 8px 0;
`;

const Subtitle = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.sizes.lg};
  margin: 0;
`;

const PlayerContainer = styled.div`
  ${theme.glassContainer.strong}
  padding: 24px;
  margin-bottom: 24px;
`;

const AudioControls = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
`;

const PlayButton = styled.button<{ $playing: boolean }>`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${props => props.$playing ? theme.colors.secondary : theme.colors.primary};
  border: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all ${theme.transitions.normal};
  box-shadow: ${theme.shadows.md};
  
  &:hover {
    background: ${props => props.$playing ? theme.colors.accent : theme.colors.secondary};
    transform: scale(1.05);
    box-shadow: ${theme.shadows.lg};
  }
`;

const SkipButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${theme.colors.glass.backgroundStrong};
  border: 1px solid ${theme.colors.glass.borderStrong};
  color: ${theme.colors.text.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  
  &:hover {
    background: ${theme.colors.glass.backgroundStrong};
    transform: scale(1.05);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const VolumeControl = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
`;

const VolumeButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.text.primary};
  cursor: pointer;
  padding: 4px;
  
  &:hover {
    color: ${theme.colors.primary};
  }
`;

const VolumeSlider = styled.input`
  width: 80px;
  height: 4px;
  background: ${theme.colors.glass.border};
  border-radius: 2px;
  outline: none;
  
  &::-webkit-slider-thumb {
    appearance: none;
    width: 16px;
    height: 16px;
    background: ${theme.colors.primary};
    border-radius: 50%;
    cursor: pointer;
  }
  
  &::-moz-range-thumb {
    width: 16px;
    height: 16px;
    background: ${theme.colors.primary};
    border-radius: 50%;
    cursor: pointer;
    border: none;
  }
`;

const ProgressContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ProgressBar = styled.div`
  flex: 1;
  height: 6px;
  background: ${theme.colors.glass.border};
  border-radius: 3px;
  position: relative;
  cursor: pointer;
`;

const ProgressFill = styled.div<{ $progress: number }>`
  height: 100%;
  width: ${props => props.$progress}%;
  background: linear-gradient(90deg, ${theme.colors.primary}, ${theme.colors.secondary});
  border-radius: 3px;
  transition: width 0.1s ease;
`;

const TimeDisplay = styled.span`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.sizes.sm};
  min-width: 45px;
  font-family: monospace;
`;

const ShareSection = styled.div`
  margin-top: 24px;
`;

const ShareTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.weights.semibold};
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ShareButtonsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 6px;
  }
`;

const ShareButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  background: ${theme.colors.glass.backgroundStrong};
  border: 1px solid ${theme.colors.glass.borderStrong};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.text.primary};
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  
  &:hover {
    background: ${theme.colors.glass.backgroundStrong};
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.md};
  }
  
  &.x-twitter {
    background: #000000;
    border-color: #333333;
    color: white;
    
    &:hover {
      background: #1a1a1a;
      border-color: #444444;
      transform: translateY(-2px) scale(1.05);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
    }
  }
  
  &.facebook {
    background: #1877f2;
    border-color: #1877f2;
    color: white;
    
    &:hover {
      background: #166fe5;
      border-color: #166fe5;
      transform: translateY(-2px) scale(1.05);
      box-shadow: 0 4px 12px rgba(24, 119, 242, 0.5);
    }
  }
  
  &.linkedin {
    background: #0a66c2;
    border-color: #0a66c2;
    color: white;
    
    &:hover {
      background: #095ba6;
      border-color: #095ba6;
      transform: translateY(-2px) scale(1.05);
      box-shadow: 0 4px 12px rgba(10, 102, 194, 0.5);
    }
  }
  
  &.email {
    background: #ea580c;
    border-color: #ea580c;
    color: white;
    
    &:hover {
      background: #dc2626;
      border-color: #dc2626;
      transform: translateY(-2px) scale(1.05);
      box-shadow: 0 4px 12px rgba(234, 88, 12, 0.5);
    }
  }
  
  &.copy.success {
    background: ${theme.colors.primary};
    color: white;
  }
`;

const LearnMoreLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 16px 24px;
  margin-top: 16px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: ${theme.borderRadius.md};
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  font-weight: 500;
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.25);
    color: white;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const RecordingInfo = styled.div`
  ${theme.glassContainer.subtle}
  padding: 16px;
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  
  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`;

const CallIdContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  
  @media (min-width: 768px) {
    align-items: flex-start;
  }
`;

const DownloadContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  width: 100%;
  
  @media (min-width: 768px) {
    width: auto;
    justify-content: flex-end;
  }
`;

const RecordingMeta = styled.div`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.sizes.sm};
`;

interface RecordingPageProps {
  recordingUrl: string;
  callData: {
    call_id: string;
    duration?: number;
    status: string;
  };
  onNewCall: () => void;
}

export const RecordingPage: React.FC<RecordingPageProps> = ({
  recordingUrl,
  callData,
  onNewCall
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [muted, setMuted] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    const handleEnded = () => setIsPlaying(false);

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [recordingUrl]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const newTime = (clickX / rect.width) * duration;
    
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const skip = (seconds: number) => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.currentTime = Math.max(0, Math.min(audio.currentTime + seconds, duration));
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const generateShareUrl = () => {
    return `${window.location.origin}/#recording-${callData.call_id}`;
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generateShareUrl());
      setCopySuccess(true);
      trackClick('copy_link_recording', 'share', { 
        share_location: 'recording_page',
        button_context: 'recording_container',
        action: 'copy_share_link',
        call_id: callData.call_id
      });
      setTimeout(() => setCopySuccess(false), 3000);
    } catch (err) {
      console.error('Failed to copy:', err);
      trackError('copy_failed', String(err), { 
        share_location: 'recording_page',
        button_context: 'recording_container',
        call_id: callData.call_id
      });
    }
  };

  const shareOnX = () => {
    trackSocialShare('x', 'after_call');
    trackClick('share_x_recording', 'social', { 
      share_platform: 'x',
      share_location: 'recording_page',
      button_context: 'recording_container',
      call_id: callData.call_id
    });
    const text = `Yobi just studied your fantasy team… should we tell you the results? 👀`;
    const url = generateShareUrl();
    window.open(`https://x.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
  };

  const shareOnFacebook = () => {
    trackSocialShare('facebook', 'after_call');
    trackClick('share_facebook_recording', 'social', { 
      share_platform: 'facebook',
      share_location: 'recording_page',
      button_context: 'recording_container',
      call_id: callData.call_id
    });
    const url = generateShareUrl();
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
  };

  const shareOnLinkedIn = () => {
    trackSocialShare('linkedin', 'after_call');
    trackClick('share_linkedin_recording', 'social', { 
      share_platform: 'linkedin',
      share_location: 'recording_page',
      button_context: 'recording_container',
      call_id: callData.call_id
    });
    const url = generateShareUrl();
    const title = 'Yobi AI Fantasy Football Expert';
    const summary = 'Just tried out Yobi\'s new AI Fantasy Football expert tool. It gave me a whole new perspective on my roster.\n\nListen to my recording here:';
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}&summary=${encodeURIComponent(summary)}`, '_blank');
  };

  const shareViaEmail = () => {
    trackSocialShare('email', 'after_call');
    trackClick('share_email_recording', 'social', { 
      share_platform: 'email',
      share_location: 'recording_page',
      button_context: 'recording_container',
      call_id: callData.call_id
    });
    const subject = 'Check out my Fantasy Football AI Expert!';
    const body = `I just talked to a fantasy football AI expert!\n\nListen to my recording here: ${generateShareUrl()}`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const downloadRecording = () => {
    trackClick('download_recording', 'call_action', { 
      action: 'download_audio',
      button_context: 'recording_container',
      call_id: callData.call_id,
      file_type: 'audio'
    });
    window.open(recordingUrl, '_blank');
  };

  return (
    <Container>
      <Header>
        <Title>🎙️ Your Recording is Ready!</Title>
        <Subtitle>Your personalized fantasy football analysis</Subtitle>
      </Header>

      <RecordingInfo>
        <CallIdContainer>
          <div style={{ fontWeight: '600', marginBottom: '4px' }}>Call ID: {callData.call_id}</div>
          <RecordingMeta>
            {callData.duration ? `Duration: ${formatTime(callData.duration)}` : 'Processing...'}
          </RecordingMeta>
        </CallIdContainer>
        <DownloadContainer>
          <ShareButton onClick={downloadRecording}>
            <Download size={16} />
            Download
          </ShareButton>
        </DownloadContainer>
      </RecordingInfo>

      <PlayerContainer>
        <AudioControls>
          <SkipButton onClick={() => skip(-10)} title="Skip back 10s">
            <SkipBack size={16} />
          </SkipButton>
          
          <PlayButton $playing={isPlaying} onClick={togglePlay}>
            {isPlaying ? <Pause size={24} /> : <Play size={24} />}
          </PlayButton>
          
          <SkipButton onClick={() => skip(10)} title="Skip forward 10s">
            <SkipForward size={16} />
          </SkipButton>

          <VolumeControl>
            <VolumeButton onClick={() => setMuted(!muted)}>
              {muted || volume === 0 ? <VolumeX size={16} /> : <Volume2 size={16} />}
            </VolumeButton>
            <VolumeSlider
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={muted ? 0 : volume}
              onChange={(e) => {
                const newVolume = parseFloat(e.target.value);
                setVolume(newVolume);
                setMuted(newVolume === 0);
              }}
            />
          </VolumeControl>
        </AudioControls>

        <ProgressContainer>
          <TimeDisplay>{formatTime(currentTime)}</TimeDisplay>
          <ProgressBar onClick={handleProgressClick}>
            <ProgressFill $progress={(currentTime / duration) * 100} />
          </ProgressBar>
          <TimeDisplay>{formatTime(duration)}</TimeDisplay>
        </ProgressContainer>
      </PlayerContainer>

      <ShareSection>
        <ShareTitle>
          <Share2 size={18} />
          Share your Recording
        </ShareTitle>
        
        <ShareButtonsContainer>
          <ShareButton className="x-twitter" onClick={shareOnX}>
            <FontAwesomeIcon icon={faXTwitter} size="lg" />
            X
          </ShareButton>
          <ShareButton className="facebook" onClick={shareOnFacebook}>
            <FontAwesomeIcon icon={faFacebook} size="lg" />
            Facebook
          </ShareButton>
          <ShareButton className="linkedin" onClick={shareOnLinkedIn}>
            <FontAwesomeIcon icon={faLinkedin} size="lg" />
            LinkedIn
          </ShareButton>
          <ShareButton className="email" onClick={shareViaEmail}>
            <FontAwesomeIcon icon={faEnvelope} size="lg" />
            Email
          </ShareButton>
        </ShareButtonsContainer>
        
        <ShareButton 
          className={copySuccess ? 'success' : ''}
          onClick={copyToClipboard}
          style={{ 
            width: '100%',
            background: copySuccess ? theme.colors.primary : theme.colors.glass.backgroundStrong,
            color: copySuccess ? 'white' : theme.colors.text.primary,
            border: `1px solid ${copySuccess ? theme.colors.primary : theme.colors.glass.borderStrong}`
          }}
        >
          {copySuccess ? (
            <>
              <Copy size={16} />
              Link Copied to Clipboard!
            </>
          ) : (
            <>
              <Copy size={16} />
              Copy Shareable Link
            </>
          )}
        </ShareButton>
      </ShareSection>

      <ShareButton 
        onClick={onNewCall}
        style={{ 
          width: '100%', 
          marginTop: '24px',
          background: theme.colors.accent,
          color: 'white',
          fontWeight: theme.typography.weights.semibold
        }}
      >
        Talk to another Expert
      </ShareButton>

      <LearnMoreLink 
        href="https://yobi.com?utm_source=fantasy_football_app&utm_medium=referral&utm_campaign=fantasyfootball"
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackClick('learn_more_yobi', 'external_link', {
          utm_campaign: 'fantasyfootball',
          utm_source: 'fantasy_football_app',
          utm_medium: 'referral',
          link_context: 'recording_page',
          call_id: callData.call_id
        })}
      >
        Learn more about Yobi →
      </LearnMoreLink>

      <audio
        ref={audioRef}
        src={recordingUrl}
        preload="metadata"
        muted={muted}
      />
    </Container>
  );
};
